import React, { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "lodash";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useToast } from "../../Utils/toastContext";

const ReinvestProgressCard = ({
  customerId,
  investmentID,
  totalAmount = 0,
  threshold = 1000,
}) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const [canReinvest, setCanReinvest] = useState(false);
  const [progress, setProgress] = useState(0);
  const [remaining, setRemaining] = useState(threshold);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const computedProgress = Math.min((totalAmount / threshold) * 100, 100);
    const remainingAmount = Math.max(threshold - totalAmount, 0);

    setProgress(computedProgress);
    setRemaining(remainingAmount);
    setCanReinvest(totalAmount >= threshold);
  }, [totalAmount, threshold]);
  const getDividend = async () => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/v1/investment-portfolio/reinvest-dividends`;
    let bodyData = {
      portfolio_id: investmentID,
      user_id: customerId,
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const HandleReinvest = useCallback(
    debounce(async (e) => {
      let toastMessage = null;
      try {
        const response = await getDividend();

        if (response.status == 200) {
          toastMessage = {
            message: "Re-investment completed successfully",
            type: "success",
          };
        } else {
          if (response.status === 401) {
            localStorage.removeItem("jwtToken");
            navigate("/login");
          }
          if (response.status === 400) {
            toastMessage = { message: response.message, type: "error" };
          }
        }
      } catch (error) {
        toastMessage = { message: error.message, type: "error" };
      } finally {
        // Show toast after loading is false
        setLoading(false);
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [investmentID, customerId] // Dependencies
  );
  return (
    <>
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="bg-[#3047EC] bg-opacity-20 px-4 py-2 rounded-lg flex flex-col justify-between h-full w-full">
        {/* Info & Button */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col space-y-1">
            <span className="text-sm font-semibold text-[#1A1A1A]">
              Total Dividend
            </span>
            <span className="text-[#3047EC] text-sm font-semibold">
              $
              {totalAmount
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </span>
          </div>

          {investmentID != -1 && (
            <button
              className={`text-xs px-3 py-1 rounded-full self-center transition-all duration-200 ${
                canReinvest
                  ? "bg-black text-white hover:bg-gray-900"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
              disabled={!canReinvest}
              onClick={() => {
                if (canReinvest) {
                  // Your reinvest logic here
                  setLoading(true);
                  HandleReinvest();
                }
              }}
            >
              Re-invest Now
            </button>
          )}
        </div>

        {/* Progress Bar */}
        {investmentID != -1 && (
          <div className="w-full space-y-1">
            {/* Labels */}
            <div className="flex justify-between items-center text-[11px] font-medium text-gray-700 dark:text-gray-300">
              <span>Progress to Reinvest</span>
              <span>{`${Math.min(progress, 100).toFixed(0)}%`}</span>
            </div>

            {/* Track */}
            <div className="relative w-full h-4 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden shadow-sm">
              {/* Fill */}
              <div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#4A6CF7] to-[#3047EC] transition-all duration-500 ease-out"
                style={{ width: `${Math.min(progress, 100)}%` }}
              ></div>
            </div>

            {/* Remaining info */}
            <div className="text-[10px] text-gray-500 dark:text-gray-400">
              {canReinvest ? (
                <span className="text-green-600 font-semibold">
                  You are now eligible to reinvest.
                </span>
              ) : (
                <span>
                  $
                  {remaining
                    .toFixed(2)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
                  remaining to reach threshold
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ReinvestProgressCard;
